import React from "react";
import { Tooltip } from "antd";
import { BankOutlined, CheckCircleFilled } from "@ant-design/icons";
import classNames from "classnames";

const Badge = ({ tone, label, tooltip, icon }) => (
  <Tooltip title={tooltip}>
    <span
      className="inline-flex items-center justify-center h-[20px] min-w-[20px] px-1.5 rounded-md text-[10px] font-semibold"
      style={{ backgroundColor: tone.bg, color: tone.fg }}
    >
      {icon ?? label}
    </span>
  </Tooltip>
);

const TONES = {
  recovery: { bg: "#FFF4E0", fg: "#9B6B00" },
  invoice: { bg: "#E6F1FC", fg: "#1558B0" },
  vetted: { bg: "#E7F7EB", fg: "#1B7A31" },
  bank: { bg: "#F1F3F7", fg: "#55607A" },
};

const SupplierOptionCard = ({ supplier, isSelected }) => {
  const address = [
    supplier?.address?.address,
    supplier?.address?.town,
    supplier?.address?.state,
    supplier?.address?.postCode,
    supplier?.address?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const hasRecoveryTypes = supplier?.recoveryServiceTypes?.length > 0;
  const currency = supplier?.bankDetails?.[0]?.currencyId;
  const country = supplier?.address?.country;

  return (
    <div
      className={classNames(
        "relative p-3 w-full text-[#1B2332] text-[13px] border rounded-[10px] transition-colors",
        {
          "border-[var(--primary-color)] bg-[#ECF9EE]": isSelected,
          "border-[#E4E7EE] bg-white hover:bg-[#FAFBFE]": !isSelected,
        },
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <Tooltip title={supplier?.name}>
            <div className="text-[14px] font-semibold text-[#0B1220] truncate">
              {supplier?.name}
            </div>
          </Tooltip>
          {address && (
            <div className="text-[12px] text-[#7A849A] truncate mt-0.5">
              {address}
            </div>
          )}
        </div>

        <div className="flex gap-1 flex-shrink-0">
          {supplier?.isRecoverySupplier && (
            <Badge
              tone={TONES.recovery}
              label="RS"
              tooltip="Recovery Supplier"
            />
          )}
          {supplier?.isCompanyInvoiceSupplier && (
            <Badge tone={TONES.invoice} label="IS" tooltip="Invoice Supplier" />
          )}
          {supplier?.isVetted && (
            <Badge tone={TONES.vetted} label="VS" tooltip="Vetted Supplier" />
          )}
          {supplier?.bankDetails?.length > 0 && (
            <Badge
              tone={TONES.bank}
              tooltip="Bank Details Available"
              icon={<BankOutlined style={{ fontSize: 11 }} />}
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-2 text-[11.5px] text-[#7A849A]">
        <span>
          Purchases:{" "}
          <span className="font-semibold text-[#0B1220]">
            {supplier?.purchasesCount ?? 0}
          </span>
        </span>
        {supplier?.type?.length > 0 && (
          <span className="flex items-center gap-1">
            Type:
            {supplier.type.map((item, index) => (
              <span
                key={index}
                className="px-2 py-0.5 rounded-md text-[11px] font-medium capitalize"
                style={{ backgroundColor: "#F1F3F7", color: "#374152" }}
              >
                {item}
              </span>
            ))}
          </span>
        )}
        {currency && (
          <span>
            Cur:{" "}
            <span className="font-semibold text-[#0B1220]">{currency}</span>
          </span>
        )}
        {country && (
          <span>
            Country:{" "}
            <span className="font-semibold text-[#0B1220]">{country}</span>
          </span>
        )}
        <span className="ml-auto cell-mono">ID: {supplier?.id}</span>
      </div>

      {hasRecoveryTypes && (
        <div className="mt-2 pt-2 border-t border-[#F1F3F7]">
          <div className="text-[11px] font-semibold text-[#7A849A] uppercase tracking-wide mb-1">
            Recovery Types
          </div>
          <div className="flex flex-wrap gap-1">
            {supplier.recoveryServiceTypes.map((item, index) => (
              <span
                key={index}
                className="px-2 py-0.5 rounded-md text-[11px] font-medium"
                style={{ backgroundColor: "#F1F3F7", color: "#374152" }}
              >
                {item.value}
              </span>
            ))}
          </div>
        </div>
      )}

      {isSelected && (
        <CheckCircleFilled className="absolute top-2 right-2 text-[var(--primary-color)] text-[16px]" />
      )}
    </div>
  );
};

export default SupplierOptionCard;
